<script setup lang="ts">
/**
 * Base Dialog Confirm
 * --------------------------------
 * Predefine dialog for cancel/confirm action
 * With heading, message and confirm + cancel buttons
 */

const {
    heading = '',
    backdrop = 'dark',
    showActionPrimary = true,
    labelActionPrimary = '',
    isDisabledActionPrimary = false,
    showActionSecondary = false,
    labelActionSecondary = '',
    isDisabledActionSecondary = false,
    classInner = '',
    isDesktopCentered = true,
    autofocusPrimary = false,
    autofocusSecondary = false,
} = defineProps<{
    heading?: string;
    backdrop?: 'dark' | 'light';
    showActionPrimary?: boolean;
    labelActionPrimary?: string;
    isDisabledActionPrimary?: boolean;
    showActionSecondary?: boolean;
    labelActionSecondary?: string;
    isDisabledActionSecondary?: boolean;
    classInner?: string;
    isDesktopCentered?: boolean;
    autofocusPrimary?: boolean;
    autofocusSecondary?: boolean;
}>();

// "closed" triggers unmounting of the dialog outside, after the close animation is done
defineEmits(['callbackPrimary', 'callbackSecondary', 'closed']);

const elDialog = useTemplateRef('elDialog');

/**
 *
 */
defineExpose({
    open: () => elDialog.value?.open(),
    close: () => elDialog.value?.close(),
    animateNextItem: (callback: () => void) => elDialog.value?.animateNextItem(callback),
});
</script>

<template>
    <BaseDialog
        ref="elDialog"
        :backdrop="backdrop"
        @closed="$emit('closed')"
    >
        <div
            class="flex h-full items-start justify-center px-5 py-6 sm:px-container-gap sm:py-10"
            :class="isDesktopCentered ? 'sm:items-center' : ''"
        >
            <div
                class="w-full max-w-(--w-max-dialog)"
                :class="classInner"
            >
                <div
                    data-transition-next
                    data-transition-slide
                    class="relative z-11 rounded bg-white"
                >
                    <!-- (X) Icon to close  -->
                    <BaseCloseButton class="absolute top-2 right-2" @click="elDialog?.close" />

                    <slot name="header">
                        <div class="flex items-center justify-between border-b border-dashed border-shade-2 px-5 pt-5 pb-3">
                            <h3 class="pr-2 typo-heading-sm">
                                {{ heading }}
                            </h3>
                        </div>
                    </slot>
                    <div class="p-5">
                        <slot />
                    </div>
                </div>

                <div
                    class="
                        mt-6
                        flex
                        items-center
                        justify-center
                    "
                >
                    <slot name="contentBelow">
                        <div
                            class="z-10 flex min-w-0 space-x-4"
                            data-transition-fade
                            data-transition-next
                        >
                            <!-- Secondary action -->
                            <BaseButton
                                v-if="showActionSecondary"
                                :disabled-visually="isDisabledActionSecondary"
                                :color-scheme="backdrop === 'dark' ? 'secondary-on-dark' : 'secondary'"
                                :autofocus="autofocusSecondary"
                                @click="$emit('callbackSecondary')"
                            >
                                {{ labelActionSecondary }}
                            </BaseButton>

                            <!-- Primary action -->
                            <BaseButton
                                v-if="showActionPrimary"
                                data-t-dialog-action-primary
                                :disabled-visually="isDisabledActionPrimary"
                                color-scheme="primary-on-dark"
                                class="shrink-0"
                                :autofocus="autofocusPrimary"
                                @click="$emit('callbackPrimary')"
                            >
                                {{ labelActionPrimary }}
                            </BaseButton>
                        </div>
                    </slot>
                </div>
            </div>
        </div>

        <slot name="outsideCard" />
    </BaseDialog>
</template>
